import React from 'react';
import { colors } from '../../theme/tokens/colors';
import { PlayIcon, PauseIcon } from '../icons';
import './PlayButtonLarge.css';

export interface PlayButtonLargeProps {
    /** Size of the button in pixels (default: 64) */
    size?: number;
    /** Whether audio is currently playing */
    isPlaying: boolean;
    /** Whether audio is loading */
    isLoading?: boolean;
    /** Progress value from 0 to 100 */
    progress: number;
    /** Click handler to toggle play/pause */
    onClick: (e: React.MouseEvent) => void;
    /** Optional className for additional styling */
    className?: string;
}

/**
 * PlayButtonLarge - A larger circular play/pause button with animated progress ring
 * Designed for SoundCloud-style audio player UI - 16px larger than the standard PlayButton
 */
export const PlayButtonLarge: React.FC<PlayButtonLargeProps> = ({
    size = 64,
    isPlaying,
    isLoading = false,
    progress,
    onClick,
    className = '',
}) => {
    // Calculate SVG dimensions
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    // Ring color based on playing/loading state
    const ringColor = isLoading ? colors.accentYellow : (isPlaying ? colors.accentYellow : colors.lightGrey);

    return (
        <button
            className={`play-button-large ${isLoading ? 'play-button-large--loading' : ''} ${className}`}
            onClick={onClick}
            style={{ width: size, height: size }}
            aria-label={isLoading ? 'Loading' : (isPlaying ? 'Pause' : 'Play')}
            type="button"
        >
            {/* Progress Ring SVG */}
            <svg
                className={`play-button-large__ring-container ${isLoading ? 'play-button-large__ring-container--spinning' : ''}`}
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
            >
                {/* Background ring (track) */}
                <circle
                    className="play-button-large__ring-bg"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                />
                {/* Progress ring */}
                <circle
                    className="play-button-large__ring-progress"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    stroke={ringColor}
                    strokeDasharray={circumference}
                    strokeDashoffset={isLoading ? circumference * 0.75 : strokeDashoffset}
                    strokeLinecap="round"
                />
            </svg>

            {/* Icon - rendered directly without wrapper */}
            <div className="play-button-large__icon">
                {isPlaying ? (
                    <PauseIcon size="l" tone="inherit" style={{ width: 38, height: 38 }} />
                ) : (
                    <PlayIcon size="l" tone="inherit" style={{ width: 38, height: 38 }} />
                )}
            </div>
        </button>
    );
};

export default PlayButtonLarge;
